import useFindProductListsQuery from '@/api/queries/useFindProductListsQuery';
import Button from '@/components/Button';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import Pagination from '@/components/Pagination';
import StatusActiveIndicator from '@/components/StatusActiveIndicator';
import { isStatusActive, Status } from '@/models/Status';
import ProductListEmptyState from '@/modules/product/components/ProductListEmptyState';
import ProductListErrorState from '@/modules/product/components/ProductListErrorState';
import {
  ProductListAddPath,
  ProductListEditPath,
  ProductListPreviewPath,
  ProductListProductListPath,
  ProductListStoreListPath,
} from '@/modules/product/paths';
import { Squares2X2Icon } from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/24/solid';
import { Link } from 'react-router-dom';

interface ProductListTableProps {
  searchQuery?: string;
  status?: Exclude<Status, 'UNKNOWN'>;
  page?: number;
  onChangePage?: (page: number) => void;
  onClearFilters?: () => void;
}

const ProductListTable = ({
  searchQuery,
  status,
  page = 1,
  onChangePage = () => {},
  onClearFilters,
}: ProductListTableProps) => {
  const { productLists, isLoading, error, itemsPerPage, invalidateQuery } =
    useFindProductListsQuery(page, searchQuery, status ? { status } : undefined);

  if (error) {
    return (
      <ProductListErrorState
        message="Unable to load Product Lists. Please try again."
        onRetry={() => void invalidateQuery()}
      />
    );
  }

  const hasActiveFilters = Boolean(searchQuery || status);

  if (!isLoading && productLists.items.length === 0) {
    return hasActiveFilters ? (
      <ProductListEmptyState
        title="No Product Lists match your search"
        description="Try a different search term or status filter."
        action={
          onClearFilters ? (
            <Button variant="secondary" onClick={onClearFilters}>
              Clear filters
            </Button>
          ) : undefined
        }
      />
    ) : (
      <ProductListEmptyState
        icon={<Squares2X2Icon className="h-10 w-10" />}
        title="No Product Lists yet"
        description="Product Lists let you group SKUs and assign them to your stores."
        action={
          <Button
            to={ProductListAddPath}
            icon={<PlusIcon className="h-5 w-5 mr-1" />}
            variant="secondary"
          >
            Create Product List
          </Button>
        }
      />
    );
  }

  return (
    <div className="table-container">
      <div className="table-wrapper">
        <table className={isLoading ? 'loading' : ''}>
          <thead>
            <tr>
              <th className="w-full min-w-48">Name</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {productLists.items.map((productList) => (
              <tr key={productList.id}>
                <td>
                  <Link
                    to={ProductListEditPath.withId(productList.id)}
                    className="text-blue-500 hover:underline"
                  >
                    {productList.name}
                  </Link>
                </td>
                <td>
                  <StatusActiveIndicator isActive={isStatusActive(productList)} />
                </td>
                <td>
                  <div className="flex w-max gap-1">
                    <Button
                      to={ProductListProductListPath.withProductListId(productList.id)}
                      variant="secondary"
                      size="xs"
                    >
                      Products
                    </Button>
                    <Button
                      to={ProductListStoreListPath.withProductListId(productList.id)}
                      variant="secondary"
                      size="xs"
                    >
                      Stores
                    </Button>
                    <Button
                      to={ProductListPreviewPath.withProductListId(productList.id)}
                      variant="secondary"
                      size="xs"
                    >
                      Preview
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {isLoading ? (
          <div role="status" className="loader">
            <SpinnerIcon />
            <span className="sr-only">Loading...</span>
          </div>
        ) : (
          ''
        )}
      </div>
      <div className="table-bottom">
        <Pagination
          currentPage={page}
          itemsPerPage={itemsPerPage}
          totalCount={productLists.totalCount || 0}
          estimatedTotalCount={productLists.estimatedTotalCount || 0}
          onClick={onChangePage}
        />
      </div>
    </div>
  );
};

export default ProductListTable;
